import { Controller } from "@hotwired/stimulus"
import {
  allocateLines,
  calculateItemGrossLine,
  calculateItemNet,
  calculateTotals,
  discountPerUnit,
  GROSS_PER_RATE,
  NET_PER_RATE,
  roundToCashGranularity,
  roundToDecimals
} from "../../utils/calculation.util"
import { formatNumber, getCurrencyDecimals, getUserLocale } from "../../utils/number-format.util"
import { formatMoney, formatPercent, serverNumberFormatFor } from "../../utils/server-format.util"

// Every line of the form, hidden ones included (they are posted too), and the lines in play.
const ITEM_ROWS = "#document_items_tr tr.invoice-item, #expense_items_tr tr.invoice-item"
const VISIBLE_ITEM_ROWS = "#document_items_tr tr.invoice-item:not(.d-none), #expense_items_tr tr.invoice-item:not(.d-none)"

// "gross_per_rate" -> "grossPerRate": the dataset key of the caption's data-gross-per-rate text
const camelize = (snake) => snake.replace(/_(\w)/g, (_, letter) => letter.toUpperCase())

// Connects to data-controller="documents--totals-calculator"
// Aggregates all invoice item totals and updates document-level totals in the DOM
export default class extends Controller {
  static outlets = ["documents--item", "documents--currency-handler", "documents--discount", "expenses--vat-deduction"]

  static targets = [
    "taxExclusive",
    "taxInclusive",
    "totalToPay",
    "totalBeforeDiscount",
    "totalBeforeDiscountLabel",
    "discountWithVat",
    "discountWithVatLabel",
    "taxBreakdown",
    "roundingBlock",
    "roundingValue",
    "paySection",
    "sourceWarning",
    "vatModeCaption"
  ]

  static values = {
    isVatPayer: Boolean,
    documentType: String,
    // The figures the extracted document printed beside its "lines do not
    // match" verdict — { subtotal, taxAmount, total }, only those it read
    // (Expense#source_totals_stated_figures). Present only with a total.
    statedSourceTotals: Object,
    // How the VAT is calculated: "net_per_rate" or "gross_per_rate". An expense only (the
    // summary partial sets it); blank on every issued document, which is always per rate from
    // the net. The expenses--vat-calculation-mode switch sets it. In gross_per_rate the lines
    // hold their printed gross and the VAT is carved out of it, whoever the supplier is: the
    // payer flag only hides the VAT block (isGrossMode).
    vatMode: String
  }

  // The last recalculate()'s pass, which getLineTaxes reuses: the expense
  // form's deduction refreshes from inside every recalculation (#updateDOM),
  // and recomputing there doubled the work of each keystroke.
  #lastTotals = null

  // Stimulus reads the values (and calls vatModeValueChanged) before connect(): the first call is
  // the page's own mode, which is not a change.
  #connected = false

  connect() {
    // Server-rendered values are authoritative on load.
    // JS recalculates only when user edits an item (via data-action on inputs).
    this.#connected = true
  }

  disconnect() {
    this.#connected = false
    this.#lastTotals = null
  }

  // The mode switch moved the expense to the other way of calculating the VAT: the summary names
  // the new method under its VAT grid, the document discount and every line follow (the hidden gross
  // posted in gross mode only), and the totals are calculated again, or they would stay one mode
  // behind. The caption carries its texts (one per mode, translated by the server) and sits outside
  // the taxBreakdown target, which #updateTaxBreakdown rewrites on every recalculation.
  vatModeValueChanged() {
    this.#nameMethod()
    if (!this.#connected) return

    // Before the totals: the block may go back to % (a cleared amount), which is what it then hands over.
    if (this.hasDocumentsDiscountOutlet) this.documentsDiscountOutlet.syncGrossMode()

    for (const row of document.querySelectorAll(ITEM_ROWS)) {
      this.#itemController(row)?.applyVatMode(this.vatModeValue)
    }
    this.recalculate()
  }

  #nameMethod() {
    if (!this.hasVatModeCaptionTarget) return

    const text = this.vatModeCaptionTarget.dataset[camelize(this.vatModeValue)]
    if (text) this.vatModeCaptionTarget.textContent = text
  }

  // Public API: is the expense calculated per gross (gross_per_rate)? The discount block reads it.
  //
  // gross_per_rate is calculated from the lines' printed grosses, whoever the supplier is: the server
  // computes in the expense's mode with each line's own rate and has no payer logic of its own (an
  // expense saved in gross mode keeps it when its supplier turns out not to be a payer, and a new
  // receipt in a workspace that is not a payer starts gross before a supplier is picked). The payer
  // flag only hides the VAT block, see #calculateTotals. Net mode is untouched.
  isGrossMode() {
    return this.vatModeValue === GROSS_PER_RATE
  }

  // Public API: Called when items change or currency changes. Every input
  // that moves the totals (an item, a discount, the currency, the VAT payer
  // toggle) comes through here, so it is also where #lastTotals is replaced.
  recalculate() {
    const totals = this.#calculateTotals()
    this.#lastTotals = totals
    this.#updateDOM(totals)
  }

  // Public API: Recalculate totals (called by currency-handler via outlet when currency changes)
  updateCurrency(_currency) {
    this.recalculate()
  }

  // EN 16931 compliant: collect rounded line nets, then category-level VAT,
  // through the same calculateTotals the util tests pin against the server
  // (TotalsCalculatorService). Also returns the visible rows and one item per
  // row (net 0 for a row whose item controller is not up yet), which
  // getLineTaxes allocates the VAT over.
  #calculateTotals() {
    const rows = this.#getAllItemRows()
    const decimals = getCurrencyDecimals(this.#getCurrency())
    const gross = this.isGrossMode()
    const rowItems = rows.map((row) => {
      const itemData = this.#getItemDataFromRow(row, gross)
      if (!itemData) return null

      // A gross line carries its real rate (the item hands it over); a net row of a non-payer counts as 0 %.
      const taxRate = gross || this.isVatPayerValue ? itemData.taxRate || 0 : 0
      const net = this.#lineNet(itemData, decimals)
      // gross_per_rate sums the lines' printed grosses per rate and carves the VAT out of them; the
      // net is what remains of a line for the VAT allocation (getLineTaxes) to share.
      return gross
        ? { net, gross: calculateItemGrossLine({ ...itemData, taxRate }, decimals), taxRate }
        : { net, taxRate }
    })
    const items = rowItems.filter(Boolean)

    // In percent mode the discount controller's getTotalDiscount() is an
    // unrounded float of the percent — hand the util the percent instead, and
    // the amount only outside percent mode. gross_per_rate takes a percentage only (an
    // amount makes the util refuse the calculation, and the server refuses the expense):
    // one that is on a refused form is left out here, the server's message stands.
    const percentage = this.#getDocumentDiscountPercent()
    const amount = percentage > 0 || gross ? 0 : this.#getDocumentDiscount()
    const totals = calculateTotals(items, { percentage, amount }, decimals, {
      mode: gross ? GROSS_PER_RATE : NET_PER_RATE
    })
    const payer = this.isVatPayerValue

    // A non-VAT payer's net rows carry taxRate 0, so the util's figures already
    // equal the net ones; #updateDOM and getLineTaxes still expect no
    // summaries and no tax for them. A gross expense keeps its real total (the printed
    // gross) for a non-payer too, with the VAT block as empty as ever.
    return {
      rows,
      items: rowItems.map((item) => item || { net: 0, taxRate: 0 }),
      decimals,
      subtotal: totals.subtotal,
      taxAmount: payer ? totals.taxAmount : 0,
      total: totals.total,
      discountAmount: totals.discountAmount,
      discountWithVat: totals.discountWithVat,
      totalBeforeDocDiscount: totals.totalBeforeDocumentDiscount,
      summaries: payer ? totals.summaries : []
    }
  }

  // Per-line VAT after the document discount, in row order, with the line's VAT
  // rate — what the deduction column multiplies and groups by — and its net
  // share, whose sign marks an advance-deduction line. Mirrors
  // Calculations::LineVatAllocator on the server.
  //
  // Reuses the last recalculate() while the same rows are on the form; before
  // the first one (the page load) or after a row came or went without one, it
  // computes afresh.
  getLineTaxes() {
    const { rows, items, decimals, summaries } = this.#currentTotals()
    const shares = allocateLines(items, summaries, decimals)
    return rows.map((row, index) => ({
      row,
      tax: shares[index].tax,
      rate: items[index].taxRate,
      net: shares[index].taxable
    }))
  }

  #currentTotals() {
    const rows = this.#getAllItemRows()
    const last = this.#lastTotals
    const unchanged = last && last.rows.length === rows.length && last.rows.every((row, index) => row === rows[index])
    return unchanged ? last : this.#calculateTotals()
  }

  // Public API: the document currency the totals are computed in.
  getCurrency() {
    return this.#getCurrency()
  }

  // Get document-level discount percentage (not amount — amount is derived)
  #getDocumentDiscountPercent() {
    if (this.hasDocumentsDiscountOutlet) {
      return this.documentsDiscountOutlet.getDiscountPercent?.() || 0
    }
    return 0
  }

  // Get document-level discount amount (backward compat)
  #getDocumentDiscount() {
    if (this.hasDocumentsDiscountOutlet) {
      return this.documentsDiscountOutlet.getTotalDiscount()
    }
    return 0
  }

  // Public API: the subtotal before the document discount, on the currency
  // grid (what a document discount is a share of outside gross mode, see
  // getDiscountableAmount).
  getSubtotalBeforeDiscount() {
    const decimals = getCurrencyDecimals(this.#getCurrency())
    const subtotal = this.#getAllItemRows().reduce((sum, row) => {
      const itemData = this.#getItemDataFromRow(row)
      return itemData ? sum + this.#lineNet(itemData, decimals) : sum
    }, 0)

    return roundToDecimals(subtotal, decimals)
  }

  // Public API: what a document discount is a share of, on the currency grid, as the server takes it
  // (TotalsCalculatorService#discountable_amount): the subtotal before the discount, or in gross_per_rate
  // the lines' gross, which the document percentage comes off. The discount controller converts an
  // amount to a percentage with it and keeps an amount within it.
  getDiscountableAmount() {
    if (!this.isGrossMode()) return this.getSubtotalBeforeDiscount()

    const decimals = getCurrencyDecimals(this.#getCurrency())
    const gross = this.#getAllItemRows().reduce((sum, row) => {
      const itemData = this.#getItemDataFromRow(row, true)
      return itemData ? sum + calculateItemGrossLine({ ...itemData, taxRate: itemData.taxRate || 0 }, decimals) : sum
    }, 0)

    return roundToDecimals(gross, decimals)
  }

  // BT-131, the line net exactly as ItemCalculatable#subtotal_after_discount:
  // (unit price − per-unit discount) × quantity, rounded once. The formula
  // carries the sign, so a negative-quantity line keeps its discount.
  #lineNet({ quantity, unitPrice, discountPerUnit }, decimals) {
    return calculateItemNet(quantity, unitPrice, discountPerUnit, decimals)
  }

  // Get item data from a row using outlet-based lookup
  #getItemDataFromRow(row, gross = false) {
    const itemController = this.#itemController(row)

    if (!itemController) {
      return null
    }

    const itemData = itemController.getItemData()

    let discountPerUnitAmount = 0
    let discountPercentage = 0
    if (itemController.hasDocumentsItemDiscountOutlet) {
      const discount = itemController.documentsItemDiscountOutlet
      if (gross) {
        // The discount of a gross line is a percentage only, and the amount a refused form may
        // still show plays no part in it. The percentage is what the gross line is priced by, so
        // only a gross row asks for it.
        discountPercentage = discount.getDiscountPercent()
        discountPerUnitAmount = discountPerUnit(itemData.unitPrice, { percent: discountPercentage })
      } else {
        discountPerUnitAmount = discount.getDiscountPerUnit()
      }
    }

    return {
      quantity: itemData.quantity,
      unitPrice: itemData.unitPrice,
      grossUnitPrice: itemData.grossUnitPrice,
      taxRate: itemData.taxRate,
      discountPerUnit: discountPerUnitAmount,
      discountPercentage
    }
  }

  // The item controller of a row, if it is up.
  #itemController(row) {
    const itemControllerElement = row.closest(`[data-controller*='documents--item']`) || row

    if (!itemControllerElement.dataset.controller?.includes("documents--item")) {
      return null
    }

    return this.application.getControllerForElementAndIdentifier(itemControllerElement, "documents--item")
  }

  // Every amount this controller prints, as number_to_currency(amount,
  // currency:) prints the server-rendered totals beside it: replayed from
  // the form's data-server-number-format (the user's regional format). Intl
  // only when a page embeds no server data.
  #moneyFormatter(currency) {
    const numberFormat = serverNumberFormatFor(this.element)
    if (numberFormat) return (value) => formatMoney(value, currency, numberFormat)

    const locale = getUserLocale()
    return (value) => formatNumber(value, locale, { style: "currency", currency })
  }

  // Get all visible invoice item rows
  #getAllItemRows() {
    return Array.from(document.querySelectorAll(VISIBLE_ITEM_ROWS)).filter((row) => {
      const destroyInput = row.querySelector('input[type="hidden"][name*="_destroy"]')
      return !destroyInput || destroyInput.value !== "true"
    })
  }

  // Update DOM elements with calculated totals
  #updateDOM(totals) {
    const { subtotal, taxAmount, total, discountAmount, discountWithVat, totalBeforeDocDiscount, summaries } = totals

    const currency = this.#getCurrency()
    const fmt = this.#moneyFormatter(currency)

    // Update pre-discount total and discount display (VAT-inclusive). In gross_per_rate the discount
    // comes off what is paid, and discountAmount is only the drop in the carved base: a 1 % discount
    // on 1,10 takes 0,01 off the total and nothing off the 0,89 base, so the rows follow the gross
    // discount there, as the saved page does. Net mode keeps the net discount.
    const showDiscount = this.isGrossMode() ? discountWithVat !== 0 : discountAmount > 0
    if (this.hasTotalBeforeDiscountTarget) {
      const preDiscountRow = this.totalBeforeDiscountTarget.closest(
        ".invoice-summary__discount-row, .document-summary__discount-line"
      )
      if (showDiscount) {
        this.totalBeforeDiscountTarget.innerText = fmt(totalBeforeDocDiscount)
        preDiscountRow?.classList.remove("d-none")
      } else {
        this.totalBeforeDiscountTarget.innerText = ""
        preDiscountRow?.classList.add("d-none")
      }
    }

    if (this.hasDiscountWithVatTarget) {
      const discountRow = this.discountWithVatTarget.closest(
        ".invoice-summary__discount-row, .document-summary__discount-line"
      )
      if (showDiscount) {
        this.discountWithVatTarget.innerText = fmt(-discountWithVat)
        if (discountRow) {
          discountRow.classList.remove("d-none")
        } else {
          this.discountWithVatTarget.classList.remove("d-none")
          if (this.hasDiscountWithVatLabelTarget) {
            this.discountWithVatLabelTarget.classList.remove("d-none")
          }
        }
      } else {
        this.discountWithVatTarget.innerText = ""
        if (discountRow) {
          discountRow.classList.add("d-none")
        } else {
          this.discountWithVatTarget.classList.add("d-none")
          if (this.hasDiscountWithVatLabelTarget) {
            this.discountWithVatLabelTarget.classList.add("d-none")
          }
        }
      }
    }

    // taxExclusive target = model `subtotal` (BT-109). The local stays
    // model-named; this assignment is the model->wire bridge.
    if (this.hasTaxExclusiveTarget) {
      this.taxExclusiveTarget.innerText = fmt(subtotal)
    }

    if (this.isVatPayerValue) {
      // Tax breakdown: show per-rate lines when multiple rates exist
      this.#updateTaxBreakdown(summaries, taxAmount, fmt)
    }

    // Grand total ("Celková suma") = taxInclusive target (BT-112). `total`
    // already equals `subtotal` for non-VAT payers' net calculation (see
    // #calculateTotals) and is the printed gross of a gross one, so write it
    // unconditionally — gating this on isVatPayerValue left the
    // grand total stale for non-VAT-payer documents after any item edit.
    // Mirrors the header-chip and "K úhrade" sinks below.
    if (this.hasTaxInclusiveTarget) {
      this.taxInclusiveTarget.innerText = fmt(total)
    }

    // Update the accordion-header grand-total chip. The chip lives
    // OUTSIDE this controller's scope (the accordion header wraps
    // around the controller's element), so we use document.querySelector
    // rather than a Stimulus target. Skipped silently if the chip
    // isn't rendered (e.g., when this section is used outside the
    // accordion wrapper).
    const headerChip = document.querySelector(".document-summary-accordion__chip")
    if (headerChip) {
      headerChip.innerText = fmt(total)
    }

    this.#updateTotalToPay(total)
    this.#updateSourceWarning(totals)

    // Expense form: the Odpočet totals follow every recalculation.
    if (this.hasExpensesVatDeductionOutlet) this.expensesVatDeductionOutlet.refresh()
  }

  // The extraction's "lines do not match the document" banner, re-judged on
  // every edit by the server's rule (Expense#source_totals_warning_resolved?):
  // hidden while the total equals the printed one exactly and the net and VAT,
  // where printed, sit within one minor unit per taxable rate group (a zdola /
  // shora rounding split). Lines corrected to the document clear it; an edit
  // away brings it back. Without a printed total the server's verdict stands.
  //
  // For a non-VAT payer #calculateTotals zeroes the VAT while the server's
  // totals keep the line rates — the divergence the summary partial's opening
  // comment documents. The banner follows whatever grand total is on screen.
  #updateSourceWarning({ subtotal, taxAmount, total, summaries, decimals }) {
    if (!this.hasSourceWarningTarget || !this.hasStatedSourceTotalsValue) return
    const printed = this.statedSourceTotalsValue
    if (printed.total == null) return

    const minor = (value) => Math.round(Number(value) * 10 ** decimals)
    const slack = Math.max(1, summaries.filter((row) => row.rate > 0).length)
    const near = (live, stated) => stated == null || Math.abs(minor(live) - minor(stated)) <= slack
    const matches =
      minor(total) === minor(printed.total) && near(subtotal, printed.subtotal) && near(taxAmount, printed.taxAmount)
    this.sourceWarningTarget.classList.toggle("d-none", matches)
  }

  // Render per-rate tax breakdown table
  #updateTaxBreakdown(summaries, taxAmount, fmt) {
    const numberFormat = serverNumberFormatFor(this.element) || {}
    if (!this.hasTaxBreakdownTarget) return

    const vatLabel = this.element.dataset.vatLabel || "DPH"
    const baseHeader = this.element.dataset.vatBaseHeader || "ZÁKLAD DPH"
    const amountHeader = this.element.dataset.vatAmountHeader || "VÝŠKA DPH"
    const totalRowLabel = this.element.dataset.vatTotalRow || "Celkom"

    const nonZeroSummaries = (summaries || []).filter((s) => s.tax !== 0 || s.taxable !== 0)
    const subtotalVal = nonZeroSummaries.reduce((sum, s) => sum + (s.taxable || 0), 0)

    let gridHtml = `<div class="vat-breakdown-grid">
      <div class="vat-breakdown-grid__row vat-breakdown-grid__header">
        <span></span><span>${baseHeader}</span><span>${amountHeader}</span>
      </div>`

    // A rate keeps up to two decimals (5.5 %, 2.1 %, 13.5 %) — formatDecimal's
    // default, the same as ApplicationHelper#format_vat_rate.
    for (const s of nonZeroSummaries) {
      gridHtml += `<div class="vat-breakdown-grid__row">
        <span class="vat-breakdown-grid__rate">${vatLabel} ${formatPercent(s.rate, numberFormat)}</span>
        <span>${fmt(s.taxable)}</span>
        <span>${fmt(s.tax)}</span>
      </div>`
    }

    gridHtml += `<div class="vat-breakdown-grid__row vat-breakdown-grid__total">
        <span class="vat-breakdown-grid__rate">${totalRowLabel}</span>
        <span>${fmt(subtotalVal)}</span>
        <span>${fmt(taxAmount)}</span>
      </div></div>`

    // Keep the grand-total div intact (it has the total target), only update the grid
    const grandTotal = this.taxBreakdownTarget.querySelector(".invoice-summary__grand-total")
    const existingGrid = this.taxBreakdownTarget.querySelector(".vat-breakdown-grid")
    if (existingGrid) {
      existingGrid.outerHTML = gridHtml
    } else if (grandTotal) {
      grandTotal.insertAdjacentHTML("beforebegin", gridHtml)
    } else {
      this.taxBreakdownTarget.innerHTML = gridHtml
    }
  }

  // Update total to pay (includes advance payment deductions, tax document items,
  // and BT-114 cash rounding when the round_total toggle is ON).
  #updateTotalToPay(baseTotal) {
    if (!this.hasTotalToPayTarget) return

    const currency = this.#getCurrency()
    const decimals = getCurrencyDecimals(currency)
    const fmt = this.#moneyFormatter(currency)

    const advancePaymentDeductionValue = document.getElementById("advance_payment_deduction_hidden")
      ? parseFloat(document.getElementById("advance_payment_deduction_hidden").value) || 0
      : 0

    const taxDocumentItemsTotal = Array.from(document.getElementsByClassName("tax-document-item__total")).reduce(
      (sum, input) => {
        const parsed = parseFloat(input.value) || 0
        return sum + parsed
      },
      0
    )

    // payable_base mirrors Ruby: (total − prepaid).round(currency_decimals)
    const payableBase = roundToDecimals(baseTotal - advancePaymentDeductionValue + taxDocumentItemsTotal, decimals)

    const roundToggle = document.getElementById("document_round_total_toggle")
    const isRounding = roundToggle?.checked ?? false

    let totalToPay = payableBase
    let roundingDelta = 0
    if (isRounding) {
      totalToPay = roundToCashGranularity(payableBase, currency)
      roundingDelta = roundToDecimals(totalToPay - payableBase, decimals)
    }

    // Show / hide the rounding row and update its delta value
    if (this.hasRoundingBlockTarget) {
      this.roundingBlockTarget.classList.toggle("d-none", !isRounding)
      if (isRounding && this.hasRoundingValueTarget) {
        this.roundingValueTarget.innerText = fmt(roundingDelta)
      }
    }

    // Show / hide the entire "K úhrade" section — visible when there is rounding
    // or a settlement deduction; hidden for plain documents with no adjustment.
    const hasDeductions = advancePaymentDeductionValue !== 0 || taxDocumentItemsTotal !== 0
    if (this.hasPaySectionTarget) {
      this.paySectionTarget.classList.toggle("d-none", !(isRounding || hasDeductions))
    }

    this.totalToPayTarget.innerText = fmt(totalToPay)
  }

  // Get currency from outlet (source of truth) with fallback
  #getCurrency() {
    if (this.hasDocumentsCurrencyHandlerOutlet && this.documentsCurrencyHandlerOutlet.hasCurrencyTarget) {
      return this.documentsCurrencyHandlerOutlet.currencyTarget.value || "EUR"
    }
    return "EUR"
  }
}
